// JobBoardViewNav.tsx - Componente principal actualizado para lazy loading

import React, { useState } from 'react';
import { FaBriefcase, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { VacantePublica } from './types';
import { JobBoardViewAccordion } from './JobBoardViewAccordion';
import { PostulationFormModal } from './PostulationFormModal';
import { useJobBoardView } from './hooks/useJobBoardView';
import './styles.css';

export const JobBoardViewNav: React.FC = () => {
  const [openSection, setOpenSection] = useState<string | null>('activas');
  const [selectedVacante, setSelectedVacante] = useState<VacantePublica | null>(null);
  const [showPostulationModal, setShowPostulationModal] = useState(false);

  // Usar el hook modular para obtener todos los datos (ahora son VacanteDetalle)
  const { 
    vacantesActivas, 
    vacantesInactivas, 
    loading, 
    hasError, 
    error,
    refetch,
    getActivasCount,
    getCerradasCount
  } = useJobBoardView();

  // Manejar postulación - recibe VacantePublica completa desde el acordeón
  const handlePostular = (vacante: VacantePublica) => {
    if (vacante.status_name !== 'EN PROCESO') return;
    
    setSelectedVacante(vacante);
    setShowPostulationModal(true);
  };

  const handlePostulationSuccess = () => {
    setShowPostulationModal(false);
    setSelectedVacante(null);
    // El hook ya se encarga de recargar los datos automáticamente
  };

  const closePostulationModal = () => {
    setShowPostulationModal(false);
    setSelectedVacante(null);
  };

  const toggleSection = (section: string) => {
    setOpenSection(openSection === section ? null : section);
  };

  // Auto-abrir sección según disponibilidad
  React.useEffect(() => {
    if (!loading && !hasError) {
      if (vacantesActivas.length > 0) {
        setOpenSection('activas');
      } else if (vacantesInactivas.length > 0) {
        setOpenSection('inactivas');
      }
    }
  }, [loading, hasError, vacantesActivas.length, vacantesInactivas.length]);

  if (loading) {
    return (
      <div className="nav-wrapper">
        <h1 className="nav-title">Vacantes abiertas</h1>
        <div className="empty-state">
          <div className="empty-state-content">
            <div className="loading-state">
              <div className="loading-content">
                <div className="loading-spinner"></div>
                <span>Cargando vacantes disponibles...</span>
              </div>
            </div>
          </div>
          </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="nav-wrapper">
        <h1 className="nav-title">Vacantes abiertas</h1>
        <div className="empty-state">
          <div className="empty-state-content">
            <h5>Error al cargar las vacantes</h5>
            <p>{error}</p>
            <button 
              onClick={refetch}
              className="modify-btn"
              style={{ marginTop: '1rem' }}
            >
              Reintentar
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Mostrar mensaje si no hay datos
  const totalVacantes = vacantesActivas.length + vacantesInactivas.length;
  
  if (totalVacantes === 0) {
    return (
      <div className="nav-wrapper">
        <h1 className="nav-title">Vacantes abiertas</h1>
        <div className="empty-state">
          <div className="empty-state-content">
            <h5>No hay vacantes disponibles</h5>
            <p>Actualmente no tenemos posiciones publicadas. Te invitamos a consultar próximamente.</p>
            <button 
              onClick={refetch}
              className="modify-btn"
              style={{ marginTop: '1rem' }}
            >
              Actualizar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="nav-wrapper">
        <h1 className="nav-title">Vacantes abiertas</h1>
        
        {/* Resumen de vacantes */}
        <div className="vacantes-summary">
          <div className="summary-card">
            <div className="summary-number active">{getActivasCount()}</div>
            <div className="summary-label">Vacantes Vigentes</div>
          </div>
          <div className="summary-card">
            <div className="summary-number inactive">{getCerradasCount()}</div>
            <div className="summary-label">Vacantes Cerradas</div>
          </div>
        </div>
        
        <div className="accordion-list">
          {/* Sección Vacantes Vigentes (Activas) */}
          {vacantesActivas.length > 0 && (
            <div className="accordion-article">
              <button
                className={`accordion-header ${openSection === 'activas' ? 'water-active' : ''}`}
                onClick={() => toggleSection('activas')}
                type="button"
              >
                <FaBriefcase className="piece-icon" />
                <span>Vacantes Vigentes ({getActivasCount()})</span>
                <span className={`accordion-chevron ${openSection === 'activas' ? 'rotate' : ''}`}>
                  {openSection === 'activas' ? <FaChevronUp /> : <FaChevronDown />}
                </span>
              </button>

              <div className={`accordion-body ${openSection === 'activas' ? 'open' : ''}`}>
                {openSection === 'activas' && (
                  <JobBoardViewAccordion
                    vacantes={vacantesActivas}
                    tipo="activas"
                    onPostular={handlePostular}
                  />
                )}
              </div>
            </div>
          )}

          {/* Sección Vacantes Cerradas (Inactivas) */}
          {vacantesInactivas.length > 0 && (
            <div className="accordion-article">
              <button
                className={`accordion-header ${openSection === 'inactivas' ? 'water-active' : ''}`}
                onClick={() => toggleSection('inactivas')}
                type="button"
              >
                <FaBriefcase className="piece-icon" />
                <span>Vacantes Cerradas ({getCerradasCount()})</span>
                <span className={`accordion-chevron ${openSection === 'inactivas' ? 'rotate' : ''}`}>
                  {openSection === 'inactivas' ? <FaChevronUp /> : <FaChevronDown />}
                </span>
              </button>

              <div className={`accordion-body ${openSection === 'inactivas' ? 'open' : ''}`}>
                {openSection === 'inactivas' && (
                  <JobBoardViewAccordion
                    vacantes={vacantesInactivas}
                    tipo="inactivas"
                    onPostular={handlePostular}
                  />
                )}
              </div>
            </div>
          )}
        </div>

        {/* Botón de actualización */}
        <div className="nav-footer">
          <button 
            onClick={refetch}
            className="refresh-btn"
            disabled={loading}
          >
            {loading ? 'Actualizando...' : 'Actualizar vacantes'}
          </button>
        </div>
      </div>

      {/* Modal de Postulación */}
      {showPostulationModal && selectedVacante && (
        <PostulationFormModal
          isOpen={showPostulationModal}
          onClose={closePostulationModal}
          vacante={selectedVacante}
          onSuccess={handlePostulationSuccess}
        />
      )}
    </>
  );
};